/*
 * Assistant citoyen — pont vers le widget de l'agent RAG « redange »
 * (rag.e-connect.lu). La balise <script> du widget est posée EN DUR dans
 * layout/layout.njk, juste avant </body> ; ce module ne fait que la piloter.
 *
 * Points d'entrée servis : la barre du hero de la home ([data-rag-search]).
 * La bulle flottante vient du widget lui-même et se règle côté admin de l'agent.
 * La loupe de l'en-tête, elle, reste sur la RECHERCHE (arbitrage du 2026-08-28) :
 * ce module n'y touche pas.
 *
 * ─── ASSISTANT RAG EN PAUSE ───
 * La pause est une case des global fields du CMS (« Désactiver l'assistant
 * citoyen »), documentée dans le commentaire qui accompagne la balise <script>
 * de layout/layout.njk. Ce module reste chargé : sans formulaire
 * [data-rag-search] rendu, il ne fait rien.
 */

const READY_TIMEOUT_MS = 3000;
const POLL_MS = 100;

function getApi() {
    return window.SearchAgentAPI || null;
}

/*
 * Le script du widget est `async` : au tout premier chargement, un clic peut
 * arriver AVANT que `SearchAgentAPI` existe. On sonde — avec un plafond, sans
 * quoi on attendrait indéfiniment un widget que `allowed_domains` a peut-être
 * refusé (403 hors des domaines déclarés, cas permanent sur la préversion).
 */
function waitForApi() {
    const immediate = getApi();
    if (immediate) return Promise.resolve(immediate);

    return new Promise((resolve) => {
        const startedAt = Date.now();
        const timer = setInterval(() => {
            const api = getApi();
            if (api || Date.now() - startedAt >= READY_TIMEOUT_MS) {
                clearInterval(timer);
                resolve(api);
            }
        }, POLL_MS);
    });
}

/*
 * Repli : la page de résultats. Elle n'est PAS optionnelle — c'est la seule
 * réponse pour un visiteur sans JavaScript, et la seule sur un domaine non
 * autorisé, où le widget reste muet sans lever la moindre erreur.
 * L'URL est rendue par le gabarit (macro searchUrl) parce que le slug diffère
 * par locale : « recherche » en français, « recherch » en luxembourgeois.
 */
function fallbackToSearchPage(query) {
    const base = document.body.dataset.searchUrl || '/fr/recherche';
    const q = query && query.trim() ? '?q=' + encodeURIComponent(query.trim()) : '';
    window.location.href = base + q;
}

/*
 * La file du widget n'est JAMAIS purgée par le widget lui-même : il ne remet
 * `pendingMessage` à zéro que sur un message `message_sent` que l'application
 * de l'iframe n'émet jamais. Or le widget détruit et recrée son iframe à chaque
 * `resize` de la fenêtre, et chaque iframe qui monte rejoue la file. Sur mobile,
 * `resize` se déclenche quand la barre d'adresse se rétracte, quand le clavier
 * s'ouvre et à chaque rotation : la même question repart toute seule.
 *
 * On purge donc au moment de la transmission RÉELLE, jamais à l'aveugle après
 * `send()` : quand l'iframe n'était pas encore là, c'est justement le rejeu qui
 * délivre la question, et purger la ferait disparaître en silence.
 */
function purgeWidgetQueue() {
    // Différé : laisse le widget transmettre sa file avant qu'on la vide, quel
    // que soit l'ordre d'enregistrement des écouteurs « message ».
    setTimeout(() => {
        const widget = window.SearchAgentWidget;
        if (widget && widget.pendingMessage) widget.pendingMessage = null;
    }, 0);
}

// Champs à vider une fois la question réellement partie — surtout pas avant :
// au premier passage le chat est verrouillé (portillon reCAPTCHA / CGU) et la
// question ne serait visible nulle part, l'usager la retaperait, et le rejeu la
// reposerait derrière. Deux fois la même question.
const pendingFields = new Set();

function flushPendingFields() {
    pendingFields.forEach((field) => { field.value = ''; });
    pendingFields.clear();
}

// Pas de filtre sur `e.origin` : le widget lui-même n'en pose pas, et un filtre
// faux remettrait la purge en panne silencieuse. Le seul effet d'un message
// forgé serait de vider un champ de saisie local.
window.addEventListener('message', (e) => {
    if (!e.data || e.data.type !== 'unlock_status_changed' || !e.data.unlocked) return;
    purgeWidgetQueue();
    flushPendingFields();
});

export async function openRagAssistant(query, field) {
    const api = await waitForApi();
    if (!api) { fallbackToSearchPage(query); return; }

    // À lire AVANT openWidget(), qui crée l'iframe : `send()` renvoie `true` sur
    // le seul état du portillon, il ne prouve PAS la transmission. Un envoi
    // direct n'arrive que si l'iframe était DÉJÀ en place.
    const widget = window.SearchAgentWidget;
    const hadIframe = !!(widget && widget.iframe);

    api.openWidget();

    const q = (query || '').trim();
    if (!q || typeof api.addMessage !== 'function') return;

    // `addMessage()` met seulement en attente malgré son nom : il faut chaîner.
    // `send()` à `false` n'est pas une erreur — le widget rejoue le message en
    // attente à la réception de `unlock_status_changed`. Ne pas ajouter de retry.
    const sent = api.addMessage(q).send();

    if (sent && hadIframe) {
        if (field) field.value = '';
        purgeWidgetQueue();
    } else if (field) {
        pendingFields.add(field);
    }
}

function initRagForms() {
    document.querySelectorAll('form[data-rag-search]').forEach((form) => {
        const field = form.querySelector('textarea[name="q"], input[name="q"]');

        form.addEventListener('submit', (e) => {
            const value = field ? field.value : '';
            // Le garde-fou de longueur minimale est posé par search-forms.js, qui
            // écoute AVANT : s'il a bloqué, on ne fait rien de plus.
            if (e.defaultPrevented) return;
            e.preventDefault();
            openRagAssistant(value, field);
        });
    });
}

document.addEventListener('DOMContentLoaded', initRagForms);
